/* Copyright (c) 2020-present CloudIO Inc */

import { ActionItemType } from '../../registry/Base.type';
import { Platform } from '../../platform/Platform';
import { ActionManager, ActionManagerFn } from '../../registry/Component.type';
import { baseActionManager, baseActionProps } from '../../registry/Components';
import { defaultValue } from './ActionGoToPage';

const ActionGoToPageManager: ActionManagerFn<'ActionGoToPage'> = (
  platform: Platform,
): ActionManager<'ActionGoToPage'> => {
  return {
    ...baseActionManager(platform, 'ActionGoToPage'),
    defaultValue,
    isChildAllowed: (page, item, childType) =>
      (['ActionSetParam'] as ActionItemType[]).includes(
        childType as ActionItemType,
      ),
    props: {
      ...baseActionProps,
      app: {
        label: 'Application ID',
        type: 'expression',
        returnType: async () => 'string',
        helpText:
          'Leave it empty if you are navigating to a page within the current application.',
      },
      page: {
        label: 'Page ID',
        type: 'expression',
        returnType: async () => 'string',
        helpText:
          'Will be nativated to the home page if this value is left blank.',
        required: true,
      },
    },
    name: 'Go To Page',
    getDisplayText: (item) => item.props.page.value,
    icon: { iconName: 'external-link' },
    description: `**Overview**<br/>
The Go To Page action is a fundamental navigation component designed to transition the user interface from the current view to a specific target page. It supports navigation both within the current application context and across different applications within the platform.<br/><br/>
This component serves as a container for navigational logic, specifically managing the transfer of data between pages. It strictly enforces the use of ActionSetParam child components, which are used to define URL parameters that accompany the navigation event. These parameters allow the destination page to initialize with specific context (accessed via $.$param.paramName).

**Key Features**<br/>
* **Data Transfer:** Explicitly designed to work with ActionSetParam children to append parameters to the navigation URL, enabling context passing (e.g., passing a recordId to a details page).
* **Cross-Application Support:** Includes an app property, allowing navigation to target pages residing in entirely different applications.
* **Dynamic Routing:** Both Application ID and Page ID are configured as expression properties, allowing for dynamic destinations based on runtime logic.
* **Strict Child Hierarchy:** Limits nested components to ActionSetParam only, ensuring the structure is used solely for parameter definition.

**Properties**<br/>
* **Application ID (app):** An expression property returning a string.
  * *Function:* Defines the target application context.
  * *Behavior:* If left empty, the navigation remains within the current application.
* **Page ID (page) (Required):** An expression property returning a string.
  * *Function:* Defines the specific target page identifier.
  * *Behavior:* If left blank, the system defaults to navigating to the application's home page.

**Use Cases**<br/>
* **Drill-Down Navigation:** Clicking a row in a grid to navigate to a "Details Page," passing the selected row's ID as a parameter.
* **Module Switching:** Redirecting a user from a "Sales App" to an "Inventory App" to check stock levels via the app property.
* **Home Redirection:** A "Cancel" or "Home" button configured with a blank Page ID to return the user to the default landing screen.`,
  };
};

export default ActionGoToPageManager;
